import { useParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiClient } from '../lib/api-client';
import { DimensionCard } from '../components/DimensionCard';
import { DimensionRubricDialog } from '../components/DimensionRubricDialog';
import { DIMENSION_WEIGHT_PERCENT } from '../lib/rubrics';
import { RecommendationsList } from '../components/RecommendationsList';
import { FrameworkBadge } from '../components/FrameworkBadge';
import { ScoreHistoryChart } from '../components/ScoreHistoryChart';
import { RubricVersionBadge } from '../components/RubricVersionBadge';
import type { Recommendation } from '@sdd-maturity/shared-types';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ScanProgress } from '../components/ScanProgress';
import { ScanFooterLine } from '../components/ScanFooterLine';
import { RescanFooter } from '../components/RescanFooter';
import { useRubricDisclosure } from '../hooks/useRubricDisclosure';
import { useRescan } from '../hooks/useRescan';
import { levelLabel, scoreColor, levelBadgeClass } from '../lib/score';
import { GHE_BASE_URL } from '../config/ghe';

export function RepoDrillDown() {
  // FR-5: the canonical leaf URL carries the owning team in the path
  // (`/teams/:teamNumber/delivery/repos/:org/:repo`). The page resolves its own
  // data from `org`/`repo`; the owning team drives the page-header breadcrumb,
  // which `useActiveScope` reads straight from the pathname — so this component
  // needs only the repo identity.
  const { org, repo } = useParams<{ org: string; repo: string }>();
  const { token } = useAuth();

  const { data: repos, isLoading: reposLoading, error: reposError } = useApi(
    (client, token) => client.getOrgRepos(org ?? '', token),
    [org],
  );

  const { data: history, isLoading: historyLoading } = useApi(
    (client, token) => client.getScoreHistory(org ?? '', repo ?? '', token),
    [org, repo],
  );

  const { activeDimension, openRubric, closeRubric } = useRubricDisclosure();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [scannedBranch, setScannedBranch] = useState<string | null>(null);

  // Persist rescan state per-repo in sessionStorage (shared re-scan state machine).
  const scanKey = `sdd-scan-${org}/${repo}`;
  const { rescanArn, rescanning, rescanError, start, handleRescanComplete } = useRescan(scanKey);

  const loading = reposLoading || historyLoading;
  const repoData = repos?.find((r) => r.repoName === repo) ?? null;

  const handleRescan = useCallback(() => {
    if (!token || !org || !repo || rescanning) return;
    void start(() => apiClient.triggerScan({ type: 'repo', orgName: org, repoName: repo }, token));
  }, [token, org, repo, rescanning, start]);

  useEffect(() => {
    if (!org || !repo || !repoData || !token) return;
    let cancelled = false;
    apiClient.getReportData(org, repo, token).then((report) => {
      if (!cancelled) {
        setRecommendations(report.recommendations ?? []);
        // Extract branch from repoPath (format: "org/repo@branch")
        const reportAny = report as unknown as Record<string, unknown>;
        const repoPath = reportAny['repoPath'] as string | undefined;
        const branchMatch = repoPath?.match(/@(.+)$/);
        if (branchMatch?.[1]) {
          setScannedBranch(branchMatch[1]);
        }
      }
    }).catch((err) => { console.warn('Failed to load report data:', err); });
    return () => { cancelled = true; };
  }, [org, repo, repoData, token]);

  if (!org || !repo) {
    return (
      <div>
        <p className="text-secondary">Invalid repository path.</p>
        <Link to="/" className="ids-btn ids-btn--secondary ids-btn--m mt-md">Back to Home</Link>
      </div>
    );
  }

  if (loading) {
    return <div data-testid="loading"><p className="text-secondary">Loading repository data...</p></div>;
  }

  if (reposError) {
    return (
      <div data-testid="error">
        <div className="card" style={{ borderColor: 'var(--genai-sem-error)' }}>
          <h2 className="text-lg font-semibold">Error Loading Repository</h2>
          <p className="text-secondary mt-sm">{reposError}</p>
          <Link to="/" className="ids-btn ids-btn--secondary ids-btn--m mt-md">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!repoData) {
    return (
      <div>
        <div className="card">
          <h2 className="text-lg font-semibold">Repository Not Found</h2>
          <p className="text-secondary mt-sm">Could not find <strong>{repo}</strong> in <strong>{org}</strong>.</p>
          <Link to="/" className="ids-btn ids-btn--secondary ids-btn--m mt-md">Back to Home</Link>
        </div>
      </div>
    );
  }

  // FR-6 AC-6.4: dashboard treats absent rubricVersion as v1 for legacy rows.
  // `rubricVersion` is optional on RepoScore — undefined for pre-cutover rows.
  const rubricVersion = repoData.rubricVersion;

  return (
    <div>
      {/* Location + up-navigation now live in the node-scoped page-header breadcrumb
          (FR-3); the old inline "Home / org / repo" nav was a duplicate. */}

      <div className="flex justify-between items-start mb-lg" style={{ flexWrap: 'wrap', gap: 'var(--genai-space-md)' }}>
        <div>
          <h2 className="text-2xl font-bold" style={{ margin: 0 }}>{repoData.repoName}</h2>
          <p className="text-secondary mt-sm">
            Organization: {repoData.orgName}
            {' '}
            <a
              href={`${GHE_BASE_URL}/${repoData.orgName}/${repoData.repoName}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ghe-link"
              className="btn-link"
              aria-label={`Open ${repoData.orgName}/${repoData.repoName} in GHE (opens in new tab)`}
            >
              GHE ↗
            </a>
            {scannedBranch && <> | Branch: <code>{scannedBranch}</code></>}
            {' | '}<FrameworkBadge framework={repoData.framework} />
            {rubricVersion === 'v2' && <> | <RubricVersionBadge rubricVersion={rubricVersion} /></>}
          </p>
        </div>
        <div className="flex items-center gap-md">
          <div className="flex items-center gap-sm">
            <span style={{ fontSize: '28px', fontWeight: 700, color: scoreColor(repoData.overallScore) }}>{repoData.overallScore}</span>
            <span className={levelBadgeClass(repoData.overallLevel)}>{levelLabel(repoData.overallLevel)}</span>
          </div>
        </div>
      </div>

      <div className="mb-lg">
        <RecommendationsList recommendations={recommendations} />
      </div>

      <h3 className="text-xl font-semibold mb-md">Dimensions</h3>
      <div className="grid grid-cols-3 mb-lg" data-testid="dimension-grid">
        {repoData.dimensions.map((dim) => (
          <DimensionCard
            key={dim.dimension}
            record={dim}
            isV1Legacy={(repoData as { _v1Legacy?: true })._v1Legacy === true}
            weightPercent={DIMENSION_WEIGHT_PERCENT[dim.dimension]}
            onOpenRubric={openRubric}
          />
        ))}
      </div>
      <DimensionRubricDialog
        dimensions={repoData.dimensions.map((dim) => dim.dimension)}
        activeDimension={activeDimension}
        phaseLabel="Delivery"
        onClose={closeRubric}
        onNavigate={openRubric}
      />

      {history && history.length > 0 && (
        <div className="mb-lg">
          <h3 className="text-lg font-semibold mb-md">Score History</h3>
          <ScoreHistoryChart data={history} showDimensions />
        </div>
      )}

      {/* Re-scan progress + error, above the "Last scan · Re-scan" footer. */}
      <RescanFooter
        progress={rescanArn ? <ScanProgress executionArn={rescanArn} onComplete={handleRescanComplete} /> : null}
        error={rescanError}
        footer={
          <ScanFooterLine
            lastScannedAt={repoData.lastScanTimestamp || null}
            busy={rescanning}
            onScan={handleRescan}
            testId="repo-rescan"
            ariaLabel="Re-scan this repository"
            align="right"
          />
        }
      />
    </div>
  );
}
